import { useState } from 'react';
import { useLoaderData, Form, useActionData, LoaderFunctionArgs, ActionFunctionArgs } from 'react-router';
import { apiClient } from '../../../lib/api-client';
import { requireUser } from '../../../lib/session.server';

interface JobBoardAccount {
  id: string;
  platform: string;
  username: string;
  is_active: boolean;
  created_at: string;
  last_sync: string;
}

export async function loader({ request }: LoaderFunctionArgs) {
  const user = await requireUser(request);
  
  try {
    const accounts = await apiClient.get('/job-board-accounts');
    return { user, accounts };
  } catch (error) {
    return { user, accounts: [] };
  }
}

export async function action({ request }: ActionFunctionArgs) {
  const formData = await request.formData();
  const intent = formData.get('intent');
  
  try {
    if (intent === 'add-account') {
      const platform = formData.get('platform');
      const username = formData.get('username');
      const password = formData.get('password');
      
      await apiClient.post('/job-board-accounts', {
        platform,
        username,
        password
      });
      
      return { success: true, message: 'Account connected successfully' };
    }
    
    if (intent === 'remove-account') {
      const accountId = formData.get('accountId');
      await apiClient.delete(`/job-board-accounts/${accountId}`);
      return { success: true, message: 'Account disconnected successfully' };
    }
    
    if (intent === 'toggle-account') {
      const accountId = formData.get('accountId');
      const isActive = formData.get('isActive') === 'true';
      
      await apiClient.put(`/job-board-accounts/${accountId}`, {
        is_active: !isActive
      });
      
      return { success: true, message: `Account ${!isActive ? 'enabled' : 'disabled'} successfully` };
    }
    
    return { error: 'Invalid action' };
  } catch (error: any) {
    return { error: error.message || 'Operation failed' };
  }
}

const SUPPORTED_PLATFORMS = [
  { id: 'linkedin', name: 'LinkedIn', icon: '🔗' },
  { id: 'indeed', name: 'Indeed', icon: '💼' },
  { id: 'glassdoor', name: 'Glassdoor', icon: '🏢' },
  { id: 'angellist', name: 'AngelList', icon: '🚀' },
  { id: 'dice', name: 'Dice', icon: '🎲' },
  { id: 'monster', name: 'Monster', icon: '👹' }
];

export function SettingsConnectionsPage() {
  const { accounts } = useLoaderData() as any;
  const actionData = useActionData() as any;
  
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  const connectedPlatforms = accounts?.map((acc: JobBoardAccount) => acc.platform) || [];

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Connected Accounts</h1>
        <p className="text-gray-600">Manage your job board and platform connections</p>
      </div>

      {actionData?.success && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
          {actionData.message}
        </div>
      )}

      {actionData?.error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {actionData.error}
        </div>
      )}

      <div className="space-y-6">
        {/* Connected Accounts */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Your Connections</h2>
            <button
              onClick={() => setShowAddForm(!showAddForm)}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
            >
              Add Connection
            </button>
          </div>
          
          {showAddForm && (
            <Form method="post" className="mb-6 p-4 bg-gray-50 rounded-lg">
              <input type="hidden" name="intent" value="add-account" />
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Platform
                  </label>
                  <select
                    name="platform"
                    value={selectedPlatform}
                    onChange={(e) => setSelectedPlatform(e.target.value)}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select a platform</option>
                    {SUPPORTED_PLATFORMS.filter(p => !connectedPlatforms.includes(p.id)).map(platform => (
                      <option key={platform.id} value={platform.id}>
                        {platform.icon} {platform.name}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Username / Email
                  </label>
                  <input
                    type="text"
                    name="username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Password
                  </label>
                  <input
                    type="password"
                    name="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Your password is encrypted and stored securely
                  </p>
                </div>
                
                <div className="flex space-x-3">
                  <button
                    type="submit"
                    className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                  >
                    Connect Account
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddForm(false);
                      setSelectedPlatform('');
                      setUsername('');
                      setPassword('');
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </Form>
          )}
          
          {accounts && accounts.length > 0 ? (
            <div className="space-y-3">
              {accounts.map((account: JobBoardAccount) => {
                const platform = SUPPORTED_PLATFORMS.find(p => p.id === account.platform);
                return (
                  <div key={account.id} className="p-4 border border-gray-200 rounded-lg">
                    <div className="flex justify-between items-start">
                      <div className="flex items-start space-x-3">
                        <span className="text-2xl">{platform?.icon}</span>
                        <div>
                          <h3 className="font-medium">{platform?.name}</h3>
                          <p className="text-sm text-gray-600">{account.username}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            Connected: {new Date(account.created_at).toLocaleDateString()}
                          </p>
                          {account.last_sync && (
                            <p className="text-xs text-gray-500">
                              Last sync: {new Date(account.last_sync).toLocaleString()}
                            </p>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <Form method="post" className="inline">
                          <input type="hidden" name="intent" value="toggle-account" />
                          <input type="hidden" name="accountId" value={account.id} />
                          <input type="hidden" name="isActive" value={account.is_active.toString()} />
                          <button
                            type="submit"
                            className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                              account.is_active
                                ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            {account.is_active ? 'Active' : 'Inactive'}
                          </button>
                        </Form>
                        
                        <Form method="post" className="inline">
                          <input type="hidden" name="intent" value="remove-account" />
                          <input type="hidden" name="accountId" value={account.id} />
                          <button
                            type="submit"
                            className="text-sm text-red-600 hover:text-red-700"
                          >
                            Disconnect
                          </button>
                        </Form>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-gray-600">
              No accounts connected yet. Add a connection to enable auto-apply and job syncing.
            </p>
          )}
        </div>

        {/* API Integrations */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-4">API Integrations</h2>
          
          <div className="space-y-4">
            <div className="p-4 border border-gray-200 rounded-lg">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="font-medium">Zapier</h3>
                  <p className="text-sm text-gray-600">Connect with 5,000+ apps</p>
                </div>
                <button
                  onClick={() => alert('Zapier integration coming soon')}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Configure
                </button>
              </div>
            </div>
            
            <div className="p-4 border border-gray-200 rounded-lg">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="font-medium">Webhooks</h3>
                  <p className="text-sm text-gray-600">Send real-time updates to your apps</p>
                </div>
                <button
                  onClick={() => alert('Webhooks coming soon')}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Configure
                </button>
              </div>
            </div>
            
            <div className="p-4 border border-gray-200 rounded-lg">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="font-medium">Developer API</h3>
                  <p className="text-sm text-gray-600">Access your data programmatically</p>
                </div>
                <button
                  onClick={() => alert('API access coming soon')}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Get API Key
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Import/Export */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-4">Import & Export</h2>
          
          <div className="space-y-4">
            <div>
              <h3 className="font-medium mb-2">Import from LinkedIn</h3>
              <p className="text-sm text-gray-600 mb-3">
                Import your profile data directly from LinkedIn
              </p>
              <button
                onClick={() => alert('LinkedIn import coming soon')}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Import Profile
              </button>
            </div>
            
            <div className="pt-4 border-t border-gray-200">
              <h3 className="font-medium mb-2">Export Data</h3>
              <p className="text-sm text-gray-600 mb-3">
                Export your profile and job data in various formats
              </p>
              <div className="space-x-3">
                <button
                  onClick={() => alert('CSV export coming soon')}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Export as CSV
                </button>
                <button
                  onClick={() => alert('JSON export coming soon')}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Export as JSON
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}